import React, {
  createContext, useEffect, useState, useRef, useMemo, useCallback
} from 'react';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { sendPNTokenToServerAPI } from '@common/api/api';
import { initFirebaseOrGetFBApp } from '../../actions/firebase_init';
import { toCamelCase, isNotificationSupported } from '../../../common/config/utils';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import ENV from '../../config/variables';
import * as permissionTriggers from './permission_triggers';

const { VAPID_KEY } = ENV;

let messaging = null;

export const NotificationContext = createContext(null);

function FirebaseMessagingProvider({ children, userId, webPushToken }) {
  const [notification, setNotification] = useState(null);
  const [permissionState, setPermissionState] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const isInitializedRef = useRef(false);
  const advisorVisitCountRef = useRef(0);
  const isPermissionGranted = typeof Notification !== 'undefined' && Notification.permission === 'granted' && userId;

  const retrieveAndSendToken = useCallback(async () => {
    if (!messaging || !userId) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    try {
      const currentToken = await getToken(messaging, { vapidKey: VAPID_KEY });
      if (currentToken) {
        if (webPushToken !== currentToken) {
          await sendPNTokenToServerAPI(currentToken);
          log(LOG_LEVELS.INFO, 'FirebaseMessaging', 'Token sent to server');
        }
      } else {
        log(LOG_LEVELS.ERROR, 'FirebaseMessaging', 'No FCM token available');
      }
    } catch (error) {
      log(LOG_LEVELS.ERROR, 'FirebaseMessaging', 'Error retrieving token', error);
    }
  }, [userId, webPushToken]);

  const requestPermission = async () => {
    if (!isNotificationSupported() || typeof Notification === 'undefined') {
      log(LOG_LEVELS.ERROR, 'FirebaseMessaging', 'Notifications not supported');
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      if (permission === 'granted') {
        log(LOG_LEVELS.INFO, 'FirebaseMessaging', 'Permission granted');
        await retrieveAndSendToken();
      }
      return permission;
    } catch (error) {
      log(LOG_LEVELS.ERROR, 'FirebaseMessaging', 'Error requesting permission', error);
      return 'denied';
    }
  };

  const initializeMessaging = () => {
    if (!isNotificationSupported()) {
      log(LOG_LEVELS.ERROR, 'FirebaseMessaging', 'Notifications not supported in this browser');
      return;
    }
    initFirebaseOrGetFBApp(() => {
      try {
        messaging = getMessaging();
        isInitializedRef.current = true;
        log(LOG_LEVELS.INFO, 'FirebaseMessaging', 'Firebase Messaging initialized successfully');

        onMessage(messaging, (payload) => {
          const { data } = payload;
          if (data) {
            const camelCaseData = toCamelCase(data);
            setNotification(camelCaseData);
          }
        });

        if (isPermissionGranted) {
          retrieveAndSendToken();
        }

        isInitializedRef.current = true;
      } catch (error) {
        log(LOG_LEVELS.ERROR, 'FirebaseMessaging', 'Error initializing messaging', error);
      }
    });
  };

  const clearNotification = useCallback(() => { setNotification(null); }, []);

  useEffect(() => {
    permissionTriggers.initializePermissionTriggers(requestPermission);
    permissionTriggers.resetState();
  }, []);

  useEffect(() => {
    if (!isInitializedRef.current) {
      initializeMessaging();
    }
  }, []);

  useEffect(() => {
    if (isPermissionGranted && messaging) {
      retrieveAndSendToken();
    }
    if (!userId) {
      permissionTriggers.resetState();
      advisorVisitCountRef.current = 0;
    }
  }, [userId, webPushToken]);

  const requestPermissionWrapper = useCallback(async (allowWithoutUserId = false) => {
    await permissionTriggers.requestPermission(userId, allowWithoutUserId);

    if (isPermissionGranted && messaging) {
      retrieveAndSendToken();
    }
  }, [userId, retrieveAndSendToken]);

  const trackAdvisorVisit = useCallback(() => {
    if (!userId) return;
    advisorVisitCountRef.current++;
    if (advisorVisitCountRef.current >= 2) {
      permissionTriggers.requestPermission(userId);
    }
  }, [userId]);

  const contextValue = useMemo(() => ({
    notification,
    clearNotification,
    requestPermission: requestPermissionWrapper,
    permissionState,
    trackAdvisorVisit
  }), [notification, clearNotification, requestPermissionWrapper, permissionState, trackAdvisorVisit]);

  return (
    <NotificationContext.Provider value={ contextValue }>
      {children}
    </NotificationContext.Provider>
  );
}

export default FirebaseMessagingProvider;
